/**
 * Route registry — single source of truth for cross-module route literals.
 *
 * Premortem K3 (docs/superpowers/specs/2026-06-10-settings-ia-redesign.md §9,
 * BE repo): 148 hardcoded route refs across 77 files made every IA move a
 * minefield. Link to pages ONLY through this module. E2E specs intentionally
 * keep literal URLs (they assert the public contract).
 *
 * OAuth rule (premortem K1): the paths returned by `oauthLanding()` are
 * registered in Azure / Google consoles — ours AND customers' (BYO apps).
 * They are FROZEN. No IA move may change them. New flows use the stable
 * /connect/callback/<provider> path once NEXT_PUBLIC_OAUTH_STABLE_CALLBACK=1
 * (ops flips it after registering the new URI in our consoles).
 */

export type OAuthProvider = 'microsoft-o365' | 'google-workspace'

export type ConnectionsSection = 'smtp' | 'microsoft' | 'google'

export const ROUTES = {
  vishing: { overview: '/vishing', voices: '/vishing/voices', scenarios: '/vishing/scenarios', lab: '/vishing/lab', campaigns: '/vishing/campaigns', templates: '/vishing/templates', optOuts: '/vishing/opt-outs', newCampaign: '/vishing/campaigns/new', reports: '/vishing/reports', settings: '/vishing/settings', phonePractice: '/vishing/phone-practice', audit: '/vishing/audit-log', gettingStarted: '/vishing/getting-started',
    reportPages: { executiveSummary: '/vishing/reports/executive-summary', trends: '/vishing/reports/trends', effectiveness: '/vishing/reports/effectiveness', objectives: '/vishing/reports/objectives', callOperations: '/vishing/reports/call-operations', repeatOffenders: '/vishing/reports/repeat-offenders', weakestLinks: '/vishing/reports/weakest-links', strongestLinks: '/vishing/reports/strongest-links' } },
  settings: {
    hub: '/settings',
    profile: '/settings/profile',
    organization: '/settings/organization',
    workspace: '/settings/workspace',
    team: '/settings/team',
    /** Customer roles: built-in and custom, what each can do (docs/product/modules/platform/2026-10-05-custom-roles-and-permissions.md). */
    roles: '/settings/roles',
    security: '/settings/security',
    auditLogs: '/settings/audit-logs',
    emailLogs: '/settings/email-logs',
    notifications: '/settings/notifications',
    brandLogos: '/settings/brand-logos',
    aiStudio: '/settings/ai-studio',
    billing: '/settings/billing',
    portal: '/settings/portal',
    // ── Delivery & Domains (the 4 pages that replaced the 7-tab mega-page) ──
    /** @deprecated Use mailDeliverySmtp / mailDeliveryMicrosoft / mailDeliveryGoogle. The page redirects to mailDeliverySmtp. */
    connections: '/settings/connections',
    connectionsSection: (s: ConnectionsSection) => `/settings/connections#${s}`,
    // ── Mail Delivery hub (SP-E Task 2) ──
    mailDeliveryHub: '/settings/mail-delivery',
    // ── Mail delivery — per-provider pages (Tasks 5-7 of SP-A) ──
    mailDeliverySmtp: '/settings/mail-delivery/smtp',
    mailDeliveryMicrosoft: '/settings/mail-delivery/microsoft-365',
    mailDeliveryGoogle: '/settings/mail-delivery/google-workspace',
    // ── Domain pages (Task 6 of SP-B — split from the retired phishing-domains page) ──
    /** Notification / Operational sending domains (emailType=Operational). */
    mailDeliveryNotificationDomains: '/settings/mail-delivery/notification-domains',
    /** Simulation / Phishing sending domains (emailType=Phishing). Reply receiving lives in the detail sheet. */
    mailDeliverySimulationDomains: '/settings/mail-delivery/simulation-domains',
    /** @deprecated Use mailDeliverySimulationDomains. The page now permanentRedirects there. Kept so the redirect page + frozen consumers still resolve. */
    phishingDomains: '/settings/phishing-domains',
    /** @deprecated Use mailDeliverySimulationDomains. Reply receiving lives in the simulation detail sheet. */
    phishingDomainsReply: '/settings/phishing-domains#reply-receiving',
    brandedDomains: '/settings/branded-domains',
    brandedDomainsSetup: '/settings/branded-domains/setup',
    // NOTE (2026-07-28): deliveryDefaults ('/settings/delivery-defaults') was
    //  REMOVED along with the page it pointed at — the module audit confirmed no
    //  code path ever read the delivery_settings that page wrote, and production
    //  showed 8 company_settings rows with 0 having delivery_settings set. The
    //  column remains (migrations are additive).
    emailDelivery: '/settings/email-delivery',
    emailGate: '/settings/email-gate',
    // ── Direct Delivery page (SP-D T7) ──
    mailDeliveryDirectDelivery: '/settings/mail-delivery/direct-delivery',
    // ── Delivery Policies pages (SP-C Task 7) ──
    mailDeliveryWhenToSend: '/settings/mail-delivery/when-to-send',
    mailDeliverySuppressionList: '/settings/mail-delivery/suppression-list',
    mailDeliveryFailureHandling: '/settings/mail-delivery/failure-handling',
    mailDeliveryCompliance: '/settings/mail-delivery/compliance',
  },
  recipients: {
    integrations: '/recipients/integrations',
    integrationProvider: (p: string) => `/recipients/integrations/${p}`,
    /** FROZEN — registered as OAuth redirect URI in external consoles. */
    integrationConfigure: (p: OAuthProvider) =>
      `/recipients/integrations/${p}/configure`,
  },
  // ── Email Security (ESA) — top-level module group ──────────────────────────
  esa: {
    /** Landing → settings-sets list. */
    root: '/esa',
    settingsSets: '/esa/settings-sets',
    settingsSet: (id: string) => `/esa/settings-sets/${id}`,
    subscriptions: '/esa/subscriptions',
    integration: '/esa/integration',
    logs: '/esa/logs',
    /** Live monitor inbox — flagged mail + would-be action previews. */
    monitor: '/esa/monitor',
    reports: '/esa/reports',
    settings: '/esa/settings',
  },
  // ── vCRO (Virtual Cyber Risk Officer) — the human-risk layer ──────────────────
  // Module key `vcro` (ROUTE_MODULE in components/module-access-guard.tsx).
  vcro: {
    root: '/vcro',
    /** Wave 2: the organisation's Riskometer is the module's landing page. */
    riskometer: '/vcro',
    breakdown: '/vcro/breakdown',
    workspaces: '/vcro/workspaces',
    people: '/vcro/people',
    person: (recipientId: string) => `/vcro/people/${encodeURIComponent(recipientId)}`,
    /** Wave 3: act. */
    watchlists: '/vcro/watchlists',
    actions: '/vcro/actions',
    settings: '/vcro/settings',
    /** Wave 4 (W4-KNOW): what feeds the score, and how it is built. */
    signals: '/vcro/signals',
    model: '/vcro/model',
    /** The module guide: what vCRO is, the day-1 path and a page-by-page reference. */
    gettingStarted: '/vcro/getting-started',
  },
  // ── Knowledge & assistants (docs/product/modules/knowledge) ───────────────────
  // Module key `knowledge` (ROUTE_MODULE in components/module-access-guard.tsx).
  knowledge: {
    base: '/settings/portal/knowledge',
    /** A document's drawer on the knowledge base (wave K3: the insights page's cited documents). */
    source: (id: string) => `/settings/portal/knowledge?source=${encodeURIComponent(id)}`,
    /** Wave K3: how the assistants are used, without names. */
    insights: '/settings/portal/knowledge/insights',
    /** The module guide: what Knowledge & assistants is, the day-1 path and a page-by-page reference. */
    gettingStarted: '/settings/portal/knowledge/getting-started',
    assistant: (surface: 'employee' | 'manager') => `/settings/portal/${surface}/assistant`,
  },
  // ── Email Remediator (ER) — top-level module group ─────────────────────────
  // Customer-facing label is always "Email Remediator"; the route/code prefix
  // stays `er`. Domain-scoped surfaces read the module domain selector.
  er: {
    /** Landing → dashboard. */
    root: '/er',
    /** Onboarding hub — setup path + what ER does. */
    gettingStarted: '/er/getting-started',
    /** In-module mail-platform connect surface (reuses the Mail Delivery connection). */
    integration: '/er/integration',
    reportedMails: '/er/reported-mails',
    reportedMail: (id: string) => `/er/reported-mails/${id}`,
    searchRemediate: '/er/search-remediate',
    searchRemediateRun: (id: string) => `/er/search-remediate/${id}`,
    threatLogs: '/er/threat-logs',
    threatLog: (id: string) => `/er/threat-logs/${id}`,
    reports: '/er/reports',
    leaderboard: '/er/leaderboard',
    logs: '/er/logs',
    /** Remediator Policy. */
    settings: '/er/settings',
    /** Dispositions & Actions catalog (the multi-surface settings shell). */
    settingsCatalogs: '/er/settings/catalogs',
    /** Install the Office add-in. */
    settingsInstallExtension: '/er/settings/install-extension',
    /** Add-in white-label branding (app name / logo / theme seeds). */
    settingsBranding: '/er/settings/branding',
    /** Colleague report tags — "N+ reported" Outlook categories (Enterprise; locked page otherwise). */
    settingsReportTags: '/er/settings/report-tags',
    auditLog: '/er/audit-log',
  },
  smishing: {
    root: '/smishing',
    gettingStarted: '/smishing/getting-started',
    campaigns: '/smishing/campaigns',
    campaign: (id: string) => `/smishing/campaigns/${id}`,
    newCampaign: '/smishing/campaigns/new',
    editCampaign: (id: string) => `/smishing/campaigns/${id}/edit`,
    templates: '/smishing/templates',
    reports: '/smishing/reports',
    // The report pages (flat, so lib/smishing/__tests__/routes.guard.spec.ts
    // proves each one opens a page).
    reportsExecutiveSummary: '/smishing/reports/executive-summary',
    reportsCampaigns: '/smishing/reports/campaigns',
    reportsFunnel: '/smishing/reports/funnel',
    reportsChannels: '/smishing/reports/channels',
    reportsTemplates: '/smishing/reports/templates',
    reportsDepartments: '/smishing/reports/departments',
    reportsPeople: '/smishing/reports/people',
    reportsTrends: '/smishing/reports/trends',
    reportsDelivery: '/smishing/reports/delivery',
    settings: '/smishing/settings',
  },
} as const

/** Stable OAuth landing route base (IA-independent, FROZEN once registered). */
export const OAUTH_CALLBACK_BASE = '/connect/callback'

/**
 * Which path to hand to the consent flow as redirect URI. Defaults to the
 * legacy per-wizard configure path (zero behavior change). Ops flips the env
 * after registering `${origin}/connect/callback/<provider>` in the Azure /
 * Google consoles for the platform apps; BYO-app customers must add it to
 * their own app registration first.
 */
export function oauthLanding(provider: OAuthProvider): string {
  const stable = process.env.NEXT_PUBLIC_OAUTH_STABLE_CALLBACK === '1'
  return stable
    ? `${OAUTH_CALLBACK_BASE}/${provider}`
    : ROUTES.recipients.integrationConfigure(provider)
}

/**
 * Param-aware mapping from the legacy 7-tab Delivery Providers mega-page to
 * the new pages. Used by the redirect page at
 * app/settings/delivery-providers/page.tsx.
 */
export function legacyDeliveryProvidersTarget(
  tab: string | null | undefined,
): string {
  switch (tab) {
    case 'domains':
      return ROUTES.settings.mailDeliverySimulationDomains
    case 'reply-receiving':
      // Reply receiving now lives inside the simulation-domains detail sheet.
      return ROUTES.settings.mailDeliverySimulationDomains
    case 'tracking-domains':
      return ROUTES.settings.brandedDomains
    case 'smtp':
      return ROUTES.settings.mailDeliverySmtp
    case 'microsoft':
      return ROUTES.settings.mailDeliveryMicrosoft
    case 'google':
      return ROUTES.settings.mailDeliveryGoogle
    // NOTE (2026-07-28): the 'defaults' case was REMOVED with the Delivery
    //  Defaults page — `?tab=defaults` now falls through to the default target
    //  rather than 404ing on a deleted route.
    default:
      return ROUTES.settings.mailDeliverySmtp
  }
}
